import { AsyncPipe } from '@angular/common';
import {
  AfterViewChecked,
  ChangeDetectorRef,
  Component,
  computed,
  ElementRef,
  inject,
  input,
  signal,
  viewChild,
} from '@angular/core';
import { outputToObservable } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { LocationDisplay } from '@app/models/address.model';
import { TextsPipe } from '@app/pipes/texts.pipe';
import { PrerolloutService } from '@app/services/prerollout.service';
import { TealiumUtagService } from '@app/services/utag.service';
import { SwissPostAddressService } from '@app/services/swisspost-address.service';
import {
  MobiUiIconComponent,
  MobiUiLoadingIndicatorComponents,
  MobiUiOverlayComponents,
  MobiUiRadioButtonComponents,
  MobiUiTextBlockComponent,
  MobiUiTypeaheadComponent,
  MobiUiTypeaheadComponents,
} from '@mobi/rwc-ui-components-ng-jslib';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  Observable,
  switchMap,
  tap,
} from 'rxjs';

@Component({
  selector: 'mobi-prerollout-overlay',
  templateUrl: './prerollout-overlay.component.html',
  styleUrls: ['./prerollout-overlay.component.scss'],
  standalone: true,
  imports: [
    AsyncPipe,
    MobiUiIconComponent,
    MobiUiLoadingIndicatorComponents,
    MobiUiOverlayComponents,
    MobiUiRadioButtonComponents,
    MobiUiTypeaheadComponents,
    TextsPipe,
    MobiUiTextBlockComponent,
  ],
})
export class PrerolloutOverlayComponent implements AfterViewChecked {
  /** The product node ID for lookup */
  readonly nodeId = input.required<string>();
  /** URL for the prerollout calculator (used when API returns true) */
  readonly prerolloutUrl = input<string>();
  /** URL for the regular calculator CTA (used when API returns false) */
  readonly regularUrl = input<string>();
  /** Target for the CTA link */
  readonly ctaTarget = input<string>();
  /** Tracking context: 'link' fires link_offsite, 'card' fires card_click */
  trackingContext = input<'link' | 'card'>('link');
  /** Name used in tracking (CTA title or card name) */
  trackingName = input<string>();

  readonly overlayButton = viewChild<ElementRef>('overlayButton');
  readonly zipTypeahead = viewChild<MobiUiTypeaheadComponent>('zipTypeahead');

  loading = signal(false);
  locations = signal<string[]>([]);
  hasSearched = signal(false);
  selectedCoverage = signal('');
  prerolloutAvailable = signal(false);
  locations$?: Observable<string[]>;
  showRadioGroup = computed(() => this.nodeId() === '10415');

  #cdr = inject(ChangeDetectorRef);
  #el = inject(ElementRef);
  #prerollout = inject(PrerolloutService);
  #router = inject(Router);
  #swissPost = inject(SwissPostAddressService);
  #utag = inject(TealiumUtagService);

  #locationLookup = new Map<string, LocationDisplay>();
  #overlayOpen = false;
  #selectedZipCode = '';
  #selectedZipAddition = '';
  #locationSelectionId = 0;

  ngAfterViewChecked(): void {
    if (this.#overlayOpen && this.zipTypeahead() && !this.locations$) {
      this.#initTypeahead();
    }
  }

  /** Opens the prerollout overlay and resets state. */
  openOverlay(): void {
    this.hasSearched.set(false);
    this.selectedCoverage.set('');
    this.prerolloutAvailable.set(false);
    this.#locationSelectionId++;
    this.locations.set([]);
    this.#overlayOpen = true;
    this.locations$ = undefined;
    this.#trackOverlayOpen();
    this.toggle();
  }

  /** Toggles the overlay visibility. */
  toggle(): void {
    setTimeout(() => this.overlayButton()?.nativeElement.click());
  }

  #initTypeahead(): void {
    this.locations$ = outputToObservable(this.zipTypeahead()!.searchTermChange).pipe(
      debounceTime(150),
      filter((term: string) => term?.trim().length >= 2),
      distinctUntilChanged(),
      tap(() => {
        this.loading.set(true);
        this.hasSearched.set(true);
      }),
      switchMap((term: string) => this.#searchLocations(term)),
      tap((results) => {
        this.loading.set(false);
        this.locations.set(results);
      })
    );
    this.#cdr.detectChanges();
  }

  /** Handles location selection from the typeahead. */
  async onLocationSelected(value: string): Promise<void> {
    const locationData = this.#locationLookup.get(value);
    if (!locationData) return;

    const selectionId = ++this.#locationSelectionId;
    this.prerolloutAvailable.set(false);
    this.selectedCoverage.set('');

    this.#selectedZipCode = locationData.zipCode;
    this.#selectedZipAddition = locationData.data?.ZipAddition || '';

    const zip = this.#selectedZipAddition
      ? `${this.#selectedZipCode}${this.#selectedZipAddition}`
      : this.#selectedZipCode;

    const isAvailable = await this.#prerollout.checkPrerollout(this.nodeId(), zip);
    if (selectionId !== this.#locationSelectionId) return;

    if (this.showRadioGroup() && isAvailable) {
      this.prerolloutAvailable.set(true);
      return;
    }

    const targetUrl = isAvailable ? this.prerolloutUrl()! : this.regularUrl()!;

    this.#trackLocationSelected(targetUrl);
    this.#navigateToUrl(targetUrl);
  }

  onCoverageSelected(value: string | null | undefined): void {
    if (!this.prerolloutAvailable()) return;

    if (value !== 'household' && value !== 'combined') return;
    const targetUrl = value === 'household' ? this.prerolloutUrl() : this.regularUrl();
    if (!targetUrl) return;

    this.selectedCoverage.set(value);
    this.#trackLocationSelected(targetUrl);
    this.#navigateToUrl(targetUrl);
  }

  #navigateToUrl(url: string): void {
    const isExternal = /^(?:[a-z]+:)?\/\//i.test(url);
    if (isExternal) {
      if (this.ctaTarget() === '_blank') {
        window.open(url);
      } else {
        window.location.href = url;
      }
    } else {
      this.#router.navigateByUrl(url, { onSameUrlNavigation: 'ignore' });
    }
  }

  #searchLocations(searchTerm: string): Observable<string[]> {
    return this.#swissPost.searchLocations(searchTerm).pipe(
      tap(({ lookupMap }) => {
        this.#locationLookup.clear();
        lookupMap.forEach((v, k) => this.#locationLookup.set(k, v));
      }),
      map(({ displayStrings }) => displayStrings)
    );
  }

  #trackOverlayOpen(): void {
    const pageData = this.#utag.getBasicPageData({});
    const domData = this.#utag.getDataFromDom(this.#el.nativeElement);

    if (this.trackingContext() === 'card') {
      this.#utag.link({
        ...pageData,
        ...domData,
        tealium_event: 'card_click',
        card_name: this.trackingName() || '',
        card_id: this.nodeId(),
      });
    } else {
      this.#utag.link({
        ...pageData,
        ...domData,
        tealium_event: 'button_click',
        button_name: this.trackingName() || 'Prämie berechnen',
      });
    }
  }

  #trackLocationSelected(targetUrl: string): void {
    const pageData = this.#utag.getBasicPageData({});
    const domData = this.#utag.getDataFromDom(this.#el.nativeElement);

    this.#utag.link({
      ...pageData,
      ...domData,
      tealium_event: 'link_offsite',
      link_action: 'click',
      link_name: 'PrerolloutOverlay',
      link_target: targetUrl,
      link_category: 'OffSiteLink - PrerolloutOverlay',
    });
  }
}
